'use client'

import { PersonStanding, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  TEXT_SIZES,
  useAccessibilitySettings,
  type TextSize,
} from './use-accessibility-settings'

const TEXT_SIZE_LABELS: Record<TextSize, string> = {
  small: 'S',
  normal: 'M',
  large: 'L',
  xlarge: 'XL',
}

type ToggleRowProps = {
  label: string
  description?: string
  checked: boolean
  onChange: (next: boolean) => void
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-popover-foreground">{label}</span>
        {description ? (
          <span className="text-xs opacity-70 text-popover-foreground">{description}</span>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        data-a11y-switch=""
        data-checked={checked}
        onClick={() => onChange(!checked)}
        className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-popover-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring data-[checked=true]:bg-primary data-[checked=false]:bg-popover"
      >
        <span
          aria-hidden
          data-a11y-switch-thumb=""
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-popover-foreground transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  )
}

export function AccessibilityWidget() {
  const { settings, ready, update, reset } = useAccessibilitySettings()
  const [open, setOpen] = useState(false)

  return (
    <div className="fixed bottom-[var(--accessibility-widget-bottom,1rem)] right-4 z-50 print:hidden">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Accessibility settings"
            className="inline-flex size-12 aspect-square items-center justify-center rounded-full border border-black bg-theme-button-secondary-background p-0 text-black transition-colors hover:bg-black hover:text-[#CE9DFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <PersonStanding aria-hidden className="size-6" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="top"
          sideOffset={8}
          className="w-80 max-w-[calc(100vw-2rem)] p-4"
        >
          {ready ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-popover-foreground">
                  Accessibility
                </h2>
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-popover-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  <RotateCcw aria-hidden className="size-3" />
                  Reset
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <span
                  id="a11y-text-size-label"
                  className="text-sm font-medium text-popover-foreground"
                >
                  Text size
                </span>
                <div
                  role="radiogroup"
                  aria-labelledby="a11y-text-size-label"
                  className="grid grid-cols-4 gap-1 rounded-md border border-border p-1"
                >
                  {TEXT_SIZES.map((size) => {
                    const isActive = settings.textSize === size
                    return (
                      <button
                        key={size}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => update('textSize', size)}
                        className={cn(
                          'rounded-sm px-2 py-1.5 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-popover-foreground hover:bg-muted',
                        )}
                      >
                        {TEXT_SIZE_LABELS[size]}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex flex-col">
                <ToggleRow
                  label="High contrast"
                  description="Stronger colors and borders"
                  checked={settings.contrast === 'high'}
                  onChange={(next) => update('contrast', next ? 'high' : 'normal')}
                />
                <ToggleRow
                  label="Reduce motion"
                  description="Disable animations and transitions"
                  checked={settings.motion === 'reduced'}
                  onChange={(next) => update('motion', next ? 'reduced' : 'normal')}
                />
                <ToggleRow
                  label="Underline links"
                  description="Always underline link text"
                  checked={settings.underlineLinks}
                  onChange={(next) => update('underlineLinks', next)}
                />
                <ToggleRow
                  label="Dyslexia-friendly font"
                  description="Use Atkinson Hyperlegible"
                  checked={settings.dyslexiaFont}
                  onChange={(next) => update('dyslexiaFont', next)}
                />
              </div>
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
